import { Button } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ICONS } from '../components/ui/icons';
import { SearchField } from '../components/ui/SearchField';
import { BrandLogo } from '../components/BrandLogo';
import { MetaConnections, type MetaOverview } from '../components/connections/MetaConnections';
import { InstagramAccounts, type InstagramAccount } from '../components/connections/InstagramAccounts';
import { usePlan } from '../contexts/PlanContext';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Conexões: só canais oficiais. WhatsApp pela Cloud API da Meta (Embedded Signup)
 * e contas do Instagram. Provedores não oficiais (QR code, pareamento) não existem mais.
 */
export function WhatsAppConnectionsPage() {
  const { selectedTenantId } = useTenant();
  const { user } = useAuth();
  const { has, ready } = usePlan();
  const showInstagram = user?.role === 'SUPERADMIN' || (ready && has('instagram'));

  // ?busca=<conexão> (ex.: vindo de uma notificação) já filtra a lista
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('busca') || '');
  useEffect(() => {
    const q = searchParams.get('busca');
    if (q === null) return;
    setSearch(q);
    searchParams.delete('busca');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // "Atualizar" remonta as seções (cada uma busca os próprios dados)
  const [reloadKey, setReloadKey] = useState(0);
  const [meta, setMeta] = useState<MetaOverview | null>(null);
  const [igAccounts, setIgAccounts] = useState<InstagramAccount[] | null>(null);

  const waCount = meta?.connections.length ?? 0;
  const igCount = igAccounts?.filter((a) => a.status === 'CONNECTED').length ?? 0;
  const summary = [
    meta ? plural(waCount, 'número de WhatsApp', 'números de WhatsApp') : null,
    showInstagram && igAccounts ? plural(igCount, 'conta do Instagram', 'contas do Instagram') : null,
  ].filter(Boolean).join(' · ');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '28px 40px 0', marginBottom: 24 }}>
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>
            Conexões
          </h2>
          <div style={{ color: 'var(--text-muted)' }}>
            {summary || 'Canais oficiais: WhatsApp pela API da Meta e Instagram.'}
          </div>
        </div>
        <Button variant="secondary" icon={ICONS.Refresh} onClick={() => setReloadKey((k) => k + 1)}>
          Atualizar
        </Button>
      </div>

      <div className="page-content space-y-8">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-full sm:min-w-[320px]">
            <SearchField
              ariaLabel="Buscar conexões"
              placeholder="Buscar por nome, número ou @usuário..."
              value={search}
              onChange={setSearch}
            />
          </div>
          {search && <Button variant="secondary" onClick={() => setSearch('')}>Limpar busca</Button>}
        </div>

        <section className="space-y-4" aria-labelledby="conexoes-whatsapp">
          <div className="flex items-center gap-3">
            <BrandLogo brand="whatsapp" size={28} rounded={6} />
            <div>
              <h4 className="text-base font-semibold" id="conexoes-whatsapp">WhatsApp · API oficial da Meta</h4>
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Envio pela Cloud API do WhatsApp, sem risco de banimento por automação. Campanhas usam templates aprovados pela Meta; as mensagens são cobradas pela Meta na sua conta.
              </div>
            </div>
          </div>
          <MetaConnections key={`wa-${selectedTenantId}-${reloadKey}`} search={search} onLoaded={setMeta} />
        </section>

        {showInstagram && (
          <section className="space-y-4" aria-labelledby="conexoes-instagram">
            <div className="flex items-center gap-3">
              <BrandLogo brand="instagram" size={28} rounded={6} />
              <div>
                <h4 className="text-base font-semibold" id="conexoes-instagram">Instagram</h4>
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  Contas profissionais para responder DMs, comentários e respostas a stories. Automações ficam em Ferramentas › Instagram.
                </div>
              </div>
            </div>
            <InstagramAccounts key={`ig-${selectedTenantId}-${reloadKey}`} search={search} onLoaded={setIgAccounts} />
          </section>
        )}
      </div>
    </>
  );
}
